/**
 * API Error Types & Normalization
 *
 * Provides a consistent error interface for all API responses,
 * normalizing Axios errors, network errors, and server errors
 * into a unified ApiError type.
 */

export interface ApiErrorResponse {
  /** HTTP status code */
  status: number;
  /** Error message for display */
  message: string;
  /** Machine-readable error code from the API */
  code?: string;
  /** Field-level validation errors */
  errors?: Record<string, string[]>;
  /** Original error for debugging */
  originalError?: unknown;
}

/**
 * Custom API Error class that extends Error with structured API error data.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly errors?: Record<string, string[]>;
  public readonly originalError?: unknown;

  constructor(response: ApiErrorResponse) {
    super(response.message);
    this.name = 'ApiError';
    this.status = response.status;
    this.code = response.code;
    this.errors = response.errors;
    this.originalError = response.originalError;
  }

  /** Whether this is a validation error (422) */
  get isValidationError(): boolean {
    return this.status === 422;
  }

  /** Whether this is an authentication error (401) */
  get isAuthError(): boolean {
    return this.status === 401;
  }

  /** Whether this is a forbidden error (403) */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Whether this is a not found error (404) */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Whether this is a rate limit error (429) */
  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** Whether this is a server error (5xx) */
  get isServerError(): boolean {
    return this.status >= 500;
  }

  /** Whether this is a network/connectivity error */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

/**
 * Default error messages by HTTP status code.
 */
const DEFAULT_ERROR_MESSAGES: Record<number, string> = {
  0: 'Network error. Please check your connection and try again.',
  400: 'The request was invalid. Please check your input.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'A conflict occurred. The resource may have been modified.',
  422: 'Please check your input and try again.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An unexpected server error occurred. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is currently unavailable. Please try again later.',
};

/**
 * Get a human-friendly error message for an HTTP status code.
 */
export function getDefaultErrorMessage(status: number): string {
  return DEFAULT_ERROR_MESSAGES[status] ?? 'An unexpected error occurred. Please try again.';
}